/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module    public/js/services/templateservice.js
 * @class     TemplateService
 * @layer     service
 * @summary   Fetches & renders admin-authored email templates (Mustache).
 *
 * @description
 * • Reads `/EmailTemplates/{id}` once, caches for the session.
 * • Renders templates with **nested** context so tags like
 *   `{{Contact.firstName}}` resolve correctly.
 * • Adds one-level “alias” keys (`firstName`, `companyName`, …) for convenience.
 *
 * ## Public API
 * – `get(id)`        → raw Firestore template         (promise)
 * – `render(id,data)`→ `{ subject, html }` rendered   (promise)
 * – `renderFromSource(source,data)`→ render raw source (promise)
 * – `invalidate(id)` → evict cached template          (void)
 *
 * ## Dependencies (DI)
 * { FirestoreService } – injected singleton (`di.fsService`)
 *
 * @author    TopSail AI
 * @version   1.1.0   // 2025-05-11 – nested context fix
 * ─────────────────────────────────────────────────────────────────────────────
 */

import Mustache from 'https://cdn.skypack.dev/mustache@4.2.0';

const DEBUG = true; // flip to false in prod
const log = (...a) => DEBUG && console.debug('[TemplateService]', ...a);

export default class TemplateService {
  /** @type {import('../core/firestoreservice.js').default} */ #fs;
  /** @type {Map<string,object>} */ #cache = new Map();

  constructor({ firestoreService }) {
    this.#fs = firestoreService;
  }

  /** Return the raw Firestore doc (cached) */
  async get(id) {
    if (this.#cache.has(id)) return this.#cache.get(id);
    const tpl = await this.#fs.getDocument(`EmailTemplates/${id}`, false);
    this.#cache.set(id, tpl);
    return tpl;
  }

  /**
   * Render subject + html with a **nested** context.
   * @param {string}  id
   * @param {object}  data – e.g. { Contact:{…}, User:{…}, Date:{…} }
   * @returns {{ subject:string, html:string }}
   */
  async render(id, data = {}) {
    const tpl = await this.get(id);
    if (!tpl) throw new Error(`Template “${id}” not found`);

    return this.renderFromSource(tpl, data);
  }

  /**
   * Render subject + html from raw template source.
   * @param {{ subject?: string, html?: string, fontFamily?: string }} source
   * @param {object} data – e.g. { Contact:{…}, User:{…}, Date:{…} }
   * @returns {{ subject:string, html:string, fontFamily: string | null }}
   */
  async renderFromSource(source, data = {}) {
    /* Build context:
       – keep nested structure for dot-notation
       – add leaf-level aliases so {{firstName}} also works */
    const ctx = { ...data };

    (function addAliases(obj = {}) {
      for (const [k, v] of Object.entries(obj)) {
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          addAliases(v); // recurse into nested objects
        } else {
          if (!(k in ctx)) ctx[k] = v; // alias if no collision
        }
      }
    })(data);

    log('ctx keys =', Object.keys(ctx));

    const subject = Mustache.render(source?.subject || '', ctx);
    const html = Mustache.render(source?.html || '', ctx);
    const fontFamily =
      typeof source?.fontFamily === 'string' ? source.fontFamily : null;

    log('rendered.subject =', subject);
    log(
      'rendered.html snippet =',
      html.slice(0, 120),
      html.length > 120 ? '…' : ''
    );

    return { subject, html, fontFamily };
  }

  /**
   * Remove a cached template so future reads refetch it.
   * @param {string} id
   * @returns {void}
   */
  invalidate(id) {
    if (!id) return;
    this.#cache.delete(id);
  }
}
